import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

interface LayoutProps {
  children: ReactNode;
}

const menuItems = [
  { href: '/dashboard', label: 'ダッシュボード', icon: '📊' },
  { href: '/', label: 'Jarvisと対話', icon: '🤖' },
  { href: '/sessions', label: 'セッション一覧', icon: '💬' },
];

const adminMenuItems = [
  { href: '/admin/users', label: 'ユーザー管理', icon: '👥' },
  { href: '/admin/contacts', label: '知人リスト', icon: '📒' },
];

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="min-h-screen flex bg-theme-main transition-colors duration-300">
      {/* サイドバー - デスクトップ */}
      <aside className="hidden md:flex w-48 flex-col bg-theme-aside text-white fixed left-0 top-0 h-screen">
        {/* ロゴ */}
        <div className="p-4 border-b border-navy-400">
          <Link to="/dashboard" className="flex items-center gap-2">
            <span className="text-2xl">🤖</span>
            <span className="font-bold text-xl text-accent">Jarvis</span>
          </Link>
        </div>
        
        {/* メニュー */}
        <nav className="flex-1 py-4">
          {menuItems.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                isActive(item.href)
                  ? 'bg-navy-400 text-primary'
                  : 'hover:bg-navy-600'
              }`}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
          
          {user?.role === 'admin' && (
            <>
              <div className="my-4 px-4">
                <div className="border-t border-navy-400"></div>
                <p className="text-xs text-gray-400 mt-4 mb-2">管理者メニュー</p>
              </div>
              {adminMenuItems.map(item => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    isActive(item.href)
                      ? 'bg-navy-400 text-primary'
                      : 'hover:bg-navy-600'
                  }`}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </>
          )}
        </nav>
        
        {/* バージョン */}
        <div className="p-4 text-xs text-gray-500">
          v1.0.0
        </div>
      </aside>

      {/* メインコンテンツ */}
      <div className="flex-1 md:ml-48">
        {/* ヘッダー */}
        <header className="h-16 bg-theme-card border-b border-theme flex items-center justify-between px-4 md:px-6 sticky top-0 z-40 transition-colors duration-300">
          {/* モバイルメニューボタン */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 hover:bg-theme-hover rounded-lg transition-colors"
          >
            <span className="text-xl">☰</span>
          </button>
          
          {/* ページタイトル */}
          <div className="flex-1 md:flex-none"></div>
          
          {/* 右側アクション */}
          <div className="flex items-center gap-4">
            {/* テーマ切り替え */}
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-theme-hover rounded-lg transition-colors"
              title={theme === 'dark' ? 'ライトモード' : 'ダークモード'}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            
            {/* プロフィールメニュー */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-theme-hover transition-colors"
              >
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-accent text-navy-900 flex items-center justify-center font-bold">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
                <span className="hidden md:block text-sm text-theme-primary">{user?.name}</span>
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-theme-card rounded-lg shadow-lg border border-theme py-2 z-50 transition-colors duration-300">
                  <div className="px-4 py-3 border-b border-theme">
                    <p className="font-medium text-theme-primary">{user?.name}</p>
                    <p className="text-sm text-theme-secondary">{user?.email}</p>
                    <p className="text-xs text-accent mt-1">{user?.role === 'admin' ? '管理者' : user?.role === 'viewer' ? '閲覧者' : 'ユーザー'}</p>
                  </div>

                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-theme-primary hover:bg-theme-hover transition-colors"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    👤 プロフィール変更
                  </Link>
                  <Link
                    to="/change-password"
                    className="block px-4 py-2 text-theme-primary hover:bg-theme-hover transition-colors"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    🔑 パスワード変更
                  </Link>

                  <div className="border-t border-theme my-2"></div>

                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 hover:bg-theme-hover transition-colors text-red-500"
                  >
                    🚪 ログアウト
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* モバイルメニュー */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="w-64 h-full bg-theme-card p-4 transition-colors duration-300" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <span className="font-bold text-xl text-accent">Jarvis</span>
                <button onClick={() => setIsMobileMenuOpen(false)} className="text-theme-primary">✕</button>
              </div>
              <nav className="space-y-2">
                {menuItems.map(item => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-theme-primary ${
                      isActive(item.href) ? 'bg-theme-hover text-accent' : 'hover:bg-theme-hover'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                ))}
                {user?.role === 'admin' && adminMenuItems.map(item => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-theme-primary ${
                      isActive(item.href) ? 'bg-theme-hover text-accent' : 'hover:bg-theme-hover'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* ページコンテンツ */}
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
